import React from "react";
import { Link } from "react-router-dom";
import "../styles/PrivacyPolicy.css";
import { translateLabel, useTranslationSnapshot } from "../translations/runtime";
import privacyLabels from "../translations/privacyLabels.json";

const translatePolicyText = (text) => translateLabel(privacyLabels[text], text);


const policyBlocks = [
  {
    paragraphs: [
      "At D2i2a w Noss, we respect the privacy of our users and are committed to protecting the personal information entrusted to us.",
      'This Privacy Policy explains how D2i2a w Noss ("D2i2a w Noss," "we," "us," or "our") collects, uses, stores, discloses, and protects information when you access or use our mobile application, website, vendor portal, driver application, and related services (collectively, the "Platform").',
      "D2i2a w Noss operates a delivery and marketplace platform connecting customers with restaurants, stores, and other participating businesses, as well as independent delivery drivers and delivery companies.",
      "Please read this Privacy Policy carefully. By accessing or using the Platform, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with its terms, please do not access or use the Platform.",
      'We reserve the right to update this Privacy Policy from time to time. When changes are made, we will update the "Last Updated" date and, where appropriate, provide additional notice through the Platform. You are encouraged to periodically review this Privacy Policy to remain informed about how we handle your information.',
    ],
  },
  {
    title: "Information Collection and Use",
    paragraphs: [
      "To provide and improve the D2i2a w Noss services, we may collect information from users depending on how they interact with the Platform.",
    ],
  },
  {
    title: "Personal Information",
    paragraphs: ["Depending on the services you use, we may collect:"],
    items: [
      "Full name",
      "Phone number",
      "Email address",
      "Date of birth where required",
      "Account credentials",
      "Profile information",
      "Saved addresses",
      "Delivery addresses",
      "Other information you voluntarily provide through the Platform",
    ],
  },
  {
    title: "Order and Transaction Information",
    paragraphs: [
      "When you place an order, we may collect and process information related to the transaction, including:",
    ],
    items: [
      "Order details",
      "Products or services purchased",
      "Vendor or store selected",
      "Order value",
      "Delivery fees",
      "Applicable service fees",
      "Discounts and promotions",
      "Payment status",
      "Order status",
      "Order history",
      "Customer notes and instructions",
      "Cancellation and refund information",
    ],
  },
  {
    title: "Location Information",
    paragraphs: [
      "D2i2a w Noss may collect location information to provide location-based services, including:",
    ],
    items: [
      "Current device location, where permission is granted",
      "Delivery location",
      "Selected addresses",
      "Vendor locations",
      "Driver locations during active delivery operations",
    ],
    afterItems: [
      "Location information may be used to identify nearby vendors, estimate delivery distances and times, support delivery operations, and improve the accuracy of the Platform.",
      "Users may manage location permissions through their device settings. Certain Platform features may not function correctly if location access is disabled.",
    ],
  },
  {
    title: "Technical and Device Information",
    paragraphs: [
      "We may automatically collect certain technical information when you use the Platform, including:",
    ],
    items: [
      "Device type",
      "Device model",
      "Operating system",
      "App version",
      "IP address",
      "Device identifiers",
      "Network information",
      "Browser information where applicable",
      "Crash reports",
      "Application usage information",
      "Log information",
    ],
    afterItems: [
      "This information helps us maintain, secure, troubleshoot, and improve the Platform.",
    ],
  },
  {
    title: "Communications",
    paragraphs: [
      "We may collect information contained in communications submitted through the Platform, including:",
    ],
    items: [
      "Customer support requests",
      "Messages and inquiries",
      "Feedback",
      "Reviews and comments",
      "Reports and complaints",
      "Other communications with D2i2a w Noss",
    ],
  },
  {
    title: "Reviews and Ratings",
    paragraphs: [
      "When users submit ratings, reviews, comments, or other feedback about vendors, products, orders, or delivery drivers, we may collect and store that information.",
      "Vendor reviews and ratings may be displayed publicly through the Platform.",
      "Driver ratings may be used for operational and performance purposes and may not be publicly displayed.",
    ],
  },
  {
    title: "Butler Service Information",
    paragraphs: [
      "When users use the Butler service, we may collect information necessary to process and fulfill the request, including:",
    ],
    items: [
      "Requested products or items",
      "Product descriptions",
      "Photos or attachments submitted by the user",
      "Delivery instructions",
      "Selected store or restaurant",
      "Additional notes",
      "Location information",
      "Communication related to the request",
    ],
    afterItems: [
      "Butler information is used to facilitate sourcing and delivery of requested items.",
    ],
  },
  {
    title: "Payment Information",
    paragraphs: [
      "Payments may be processed through third-party payment service providers.",
      "Depending on the payment method, D2i2a w Noss may collect information such as:",
    ],
    items: [
      "Payment method",
      "Transaction reference",
      "Payment status",
      "Transaction amount",
      "Refund information",
    ],
    afterItems: [
      "Where payment processing is performed by a third-party payment provider, payment information may be processed directly by that provider in accordance with its own privacy policy and security practices.",
      "D2i2a w Noss does not necessarily store complete payment card information on its own systems.",
    ],
  },
  {
    title: "How We Use Your Information",
    paragraphs: [
      "We may use the information we collect for the following purposes:",
    ],
  },
  {
    title: "To Provide Our Services",
    paragraphs: ["We use information to:"],
    items: [
      "Create and manage user accounts",
      "Process and manage orders",
      "Connect customers with vendors",
      "Facilitate delivery services",
      "Connect orders with available delivery drivers or delivery companies",
      "Process Butler requests",
      "Provide order tracking",
      "Calculate estimated delivery times",
      "Provide customer support",
      "Process payments, refunds, and applicable transactions",
      "Deliver products and services requested by users",
    ],
  },
  {
    title: "To Improve Our Services",
    paragraphs: ["We may analyze usage and operational information to:"],
    items: [
      "Improve the functionality of the Platform",
      "Improve vendor and customer experiences",
      "Improve delivery operations",
      "Identify technical issues",
      "Understand usage patterns",
      "Develop new features and services",
      "Improve search, discovery, and recommendations",
      "Analyze Platform performance",
    ],
  },
  {
    title: "To Communicate With You",
    paragraphs: ["We may use your contact information to:"],
    items: [
      "Send order confirmations",
      "Send order status updates",
      "Send delivery notifications",
      "Send account-related communications",
      "Send customer support responses",
      "Request feedback and reviews",
      "Notify users about important Platform changes",
      "Send promotional communications where permitted",
    ],
    afterItems: [
      "Users may manage certain notification preferences through the Platform or their device settings.",
    ],
  },
  {
    title: "To Provide Location-Based Services",
    paragraphs: ["Location information may be used to:"],
    items: [
      "Display nearby vendors",
      "Determine delivery availability",
      "Estimate delivery distance and time",
      "Facilitate driver navigation and delivery",
      "Match orders with available delivery resources",
      "Improve delivery operations",
    ],
  },
  {
    title: "To Maintain Security",
    paragraphs: ["We may use information to:"],
    items: [
      "Detect and prevent fraud",
      "Detect unauthorized activity",
      "Protect accounts",
      "Investigate suspicious transactions",
      "Prevent abuse of the Platform",
      "Protect users, vendors, drivers, and the Platform",
      "Enforce our terms and policies",
    ],
  },
  {
    title: "To Manage Reviews and Feedback",
    paragraphs: ["We may use ratings, reviews, comments, and feedback to:"],
    items: [
      "Display vendor ratings and reviews",
      "Evaluate service quality",
      "Improve vendor performance",
      "Monitor delivery quality",
      "Investigate complaints",
      "Maintain Platform standards",
    ],
  },
  {
    title: "For Analytics and Reporting",
    paragraphs: [
      "We may use aggregated or appropriately de-identified information to understand:",
    ],
    items: [
      "Order volume",
      "Vendor performance",
      "Delivery performance",
      "Platform usage",
      "Customer behavior",
      "Operational trends",
    ],
    afterItems: [
      "Such information may be used for internal analytics, reporting, planning, and business improvement.",
    ],
  },
  {
    title: "Data Sharing and Disclosure",
    paragraphs: [
      "D2i2a w Noss may share information when necessary to provide the Platform and its services.",
    ],
  },
  {
    title: "Vendors and Stores",
    paragraphs: [
      "When a customer places an order, information necessary to prepare and fulfill that order may be shared with the relevant vendor.",
      "This may include:",
    ],
    items: [
      "Customer name",
      "Order details",
      "Delivery information",
      "Customer instructions",
      "Contact information where necessary to fulfill the order",
    ],
    afterItems: [
      "Vendors are responsible for handling information made available to them through the Platform in accordance with applicable requirements and their contractual obligations.",
    ],
  },
  {
    title: "Independent Delivery Drivers and Delivery Companies",
    paragraphs: [
      "Information necessary to complete a delivery may be shared with the assigned delivery driver or delivery company.",
      "This may include:",
    ],
    items: [
      "Pickup location",
      "Delivery location",
      "Order information necessary for fulfillment",
      "Customer delivery instructions",
      "Contact information where necessary for delivery",
    ],
    afterItems: [
      "Drivers and delivery companies may only use this information for legitimate delivery and operational purposes.",
    ],
  },
  {
    title: "Payment Service Providers",
    paragraphs: [
      "We may share necessary transaction information with third-party payment providers to process payments, refunds, and other financial transactions.",
    ],
  },
  {
    title: "Technology and Service Providers",
    paragraphs: [
      "We may use third-party service providers for services such as:",
    ],
    items: [
      "Cloud hosting",
      "Data storage",
      "Analytics",
      "Notifications",
      "Payment processing",
      "Security",
      "Customer support",
      "Communication services",
      "Technical infrastructure",
    ],
    afterItems: [
      "These providers may process information on our behalf as necessary to provide their services.",
    ],
  },
  {
    title: "Legal Requirements",
    paragraphs: [
      "We may disclose information where we believe disclosure is necessary or required to:",
    ],
    items: [
      "Comply with applicable laws or legal processes",
      "Respond to lawful requests from authorities",
      "Protect the rights, property, or safety of D2i2a w Noss",
      "Protect users, vendors, drivers, or other parties",
      "Investigate fraud or security incidents",
      "Enforce our Terms and Conditions or other policies",
    ],
  },
  {
    title: "Business Transfers",
    paragraphs: [
      "If D2i2a w Noss or part of its business is involved in a merger, acquisition, restructuring, sale, or other business transaction, information may be transferred as part of that transaction, subject to applicable legal requirements.",
    ],
  },
  {
    title: "Data Security",
    paragraphs: [
      "We take reasonable technical and organizational measures to protect information against unauthorized access, loss, misuse, alteration, or disclosure.",
      "These measures may include:",
    ],
  },
  {
    title: "Encryption",
    paragraphs: [
      "We use appropriate encryption and security measures to protect information during transmission and, where appropriate, while stored.",
    ],
  },
  {
    title: "Access Controls",
    paragraphs: [
      "Access to personal information is limited to authorized personnel and service providers who require access to perform their responsibilities.",
    ],
  },
  {
    title: "Monitoring and Security",
    paragraphs: [
      "We may use technical monitoring and security controls to detect suspicious activity, unauthorized access, and potential security threats.",
    ],
  },
  {
    title: "Data Retention",
    paragraphs: [
      "We retain information for as long as reasonably necessary to provide our services, maintain business and transaction records, comply with applicable legal requirements, resolve disputes, enforce agreements, and protect the Platform.",
      "Retention periods may vary depending on the type of information and the purpose for which it was collected.",
      "However, no method of transmission over the Internet or method of electronic storage is completely secure. While we take reasonable measures to protect information, we cannot guarantee absolute security.",
    ],
  },
  {
    title: "User Rights",
    paragraphs: [
      "Depending on applicable law, users may have rights regarding their personal information, including the right to:",
    ],
    items: [
      "Access personal information held about them",
      "Request correction of inaccurate information",
      "Request deletion of personal information where applicable",
      "Request restriction of certain processing",
      "Object to certain processing",
      "Request a copy of certain information in a structured format where applicable",
      "Withdraw consent where processing is based on consent",
    ],
    afterItems: [
      "Certain information may need to be retained where required for legal, regulatory, security, fraud prevention, or legitimate business purposes.",
      "To exercise applicable privacy rights or submit a privacy-related request, users may contact D2i2a w Noss through the contact details provided within the Platform or through the designated privacy contact address.",
    ],
  },
  {
    title: "Mobile Device Access",
    paragraphs: [
      "D2i2a w Noss may request access or permission to certain features of your mobile device depending on the services you use.",
      "These permissions may include:",
    ],
    items: [
      "Location",
      "Camera",
      "Photos or storage",
      "Notifications",
      "Microphone where required for a specific feature",
    ],
    afterItems: [
      "Users can manage these permissions through their device settings.",
      "Disabling certain permissions may limit the availability or functionality of related Platform features.",
    ],
  },
  {
    title: "Push Notifications",
    paragraphs: [
      "D2i2a w Noss may send push notifications related to:",
    ],
    items: [
      "Account activity",
      "Order status",
      "Delivery updates",
      "Payment status",
      "Customer support",
      "Review requests",
      "Promotional offers",
      "Other Platform-related communications",
    ],
    afterItems: [
      "Users may manage notification permissions through their device settings and, where available, through notification preferences within the Platform.",
      "Certain essential service notifications may be necessary for the proper operation of the Platform.",
    ],
  },
  {
    title: "Marketing and Promotional Communications",
    paragraphs: [
      "D2i2a w Noss may send promotional communications regarding:",
    ],
    items: [
      "Offers",
      "Discounts",
      "New vendors",
      "New services",
      "Campaigns",
      "Platform features",
    ],
    afterItems: [
      "Where required, users will be provided with appropriate options to manage or opt out of promotional communications.",
      "Opting out of promotional communications does not necessarily prevent users from receiving essential service communications related to their account or orders.",
    ],
  },
  {
    title: "Children and Families",
    paragraphs: [
      "D2i2a w Noss is a general marketplace and delivery platform and is not specifically directed toward children.",
      "Users should not provide personal information belonging to another individual, including a child, unless they are authorized to do so.",
      "Where the Platform allows an adult to place an order or provide delivery information on behalf of another person, the person providing the information is responsible for ensuring that they have the appropriate authority to provide such information.",
      "D2i2a w Noss does not knowingly seek to collect unnecessary personal information from children.",
      "If we become aware that personal information has been collected from a child in circumstances where such collection is not permitted, we may take appropriate steps to delete or restrict that information.",
    ],
  },
  {
    title: "Vendor, Driver, and Delivery Company Information",
    paragraphs: [
      "D2i2a w Noss operates a multi-party platform involving customers, vendors, independent delivery drivers, and delivery companies.",
      "Information associated with vendor, driver, and delivery company accounts may include:",
    ],
    items: [
      "Identity information",
      "Contact information",
      "Business information",
      "Legal and verification documents",
      "Location information",
      "Operational information",
      "Order and transaction information",
      "Performance information",
      "Ratings and reviews",
      "Account and activity history",
    ],
    afterItems: [
      "Such information is used to verify accounts, operate the Platform, facilitate transactions and deliveries, monitor service quality, prevent fraud, and maintain Platform security and standards.",
    ],
  },
  {
    title: "Reviews and Publicly Displayed Information",
    paragraphs: [
      "Information that users voluntarily submit for public display, such as vendor reviews, ratings, comments, or other publicly visible content, may be visible to other users of the Platform.",
      "Users should avoid including sensitive personal information, private contact details, financial information, or other information they do not wish to make publicly available in reviews or comments.",
      "D2i2a w Noss may moderate, restrict, or remove content that violates applicable policies or Platform standards.",
    ],
  },
  {
    title: "Third-Party Services and External Links",
    paragraphs: [
      "The Platform may integrate with or provide access to third-party services.",
      "Third-party services may have their own privacy policies and terms. D2i2a w Noss is not responsible for the privacy practices of third-party services that operate independently from the Platform.",
      "Users are encouraged to review the privacy policies of third-party services before providing information to them.",
    ],
  },
  {
    title: "Changes to This Privacy Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time.",
      'When changes are made, we will update the "Last Updated" date of this Privacy Policy and may provide additional notice where appropriate.',
      "Your continued use of the Platform after an updated Privacy Policy becomes effective constitutes acknowledgment of the updated policy, subject to applicable legal requirements.",
    ],
  },
  {
    title: "Contact Us",
    paragraphs: [
      "If you have any questions, concerns, requests, or suggestions regarding this Privacy Policy or the way D2i2a w Noss handles personal information, please contact us through the contact information provided in the Platform.",
      "D2i2a w Noss",
    ],
    email: "mehsen.sleiman@javelynx.com",
  },
];

function PrivacyPolicy() {
  useTranslationSnapshot();
  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-shell">
        <div className="privacy-policy-header">
          <Link className="privacy-policy-back" to="/sign-in">
            {translateLabel("txt_web_vendor_back_to_sign_in", "Back to Sign In")}
          </Link>
          <div className="privacy-policy-brand">{translateLabel("txt_end_user_privacy_policy_255", "D2i2a w Noss")}</div>
          <h1>{translateLabel("lnk_privacy_policy", "Privacy Policy")}</h1>
          <p>{translateLabel("txt_end_user_privacy_policy_001", "Last Updated: 17/8/2026")}</p>
        </div>

        <div className="privacy-policy-content">
          {policyBlocks.map((block, index) => (
            <div className="privacy-policy-block" key={`${block.title || "intro"}-${index}`}>
              {block.title ? <h2>{translatePolicyText(block.title)}</h2> : null}
              {block.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{translatePolicyText(paragraph)}</p>
              ))}
              {block.items ? (
                <ul>
                  {block.items.map((item) => (
                    <li key={item}>{translatePolicyText(item)}</li>
                  ))}
                </ul>
              ) : null}
              {block.afterItems?.map((paragraph) => (
                <p key={paragraph}>{translatePolicyText(paragraph)}</p>
              ))}
              {block.email ? (
                <p> {translateLabel("txt_end_user_email", "Email:")} <a href={`mailto:${block.email}`}>{block.email}</a>
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
